// Central API client — always calls the real FastAPI backend.

import { getItem, STORAGE_KEYS } from './storage.js';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

function authHeader() {
  const session = getItem(STORAGE_KEYS.SESSION);
  if (!session?.sessionToken) return {};
  return { Authorization: `Bearer ${session.sessionToken}` };
}


async function request(path, { method = 'GET', body, auth = false } = {}) {
  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(auth ? authHeader() : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try { data = await response.json(); } catch { data = null; }

  if (!response.ok) {
    throw new ApiError(data?.detail || `Request failed with status ${response.status}`, response.status);
  }
  return data;
}

export const apiClient = {
  async login(payload) {
    return request('/auth/login', { method: 'POST', body: payload });
  },

  async changePassword(currentPassword, newPassword) {
    return request('/auth/change-password', {
      method: 'POST',
      auth: true,
      body: { current_password: currentPassword, new_password: newPassword },
    });
  },

  async listParticipants() {
    return request('/meta/participants');
  },

  async listPlans() {
    return request('/meta/plans');
  },

  async getQueue() {
    return request('/queue', { auth: true });
  },

  async approveQueueEntry(entryId, note) {
    return request(`/queue/${entryId}/approve`, { method: 'POST', body: { note }, auth: true });
  },

  async denyQueueEntry(entryId, note) {
    return request(`/queue/${entryId}/deny`, { method: 'POST', body: { note }, auth: true });
  },

  async cancelQueueEntry(entryId) {
    return request(`/queue/${entryId}/cancel`, { method: 'POST', body: {}, auth: true });
  },

  async getQueueEntryDocs(entryId) {
    return request(`/queue/${entryId}/docs`, { auth: true });
  },

  async approveQueueEntryDocs(entryId, note) {
    return request(`/queue/${entryId}/approve-docs`, { method: 'POST', body: { note }, auth: true });
  },

  async getAuditLog() {
    return request('/admin/audit', { auth: true });
  },

  async downloadAuditLogCsv() {
    const session = getItem(STORAGE_KEYS.SESSION);
    const token = session?.sessionToken || '';
    const res = await fetch(`${BASE_URL}/admin/audit/export?format=csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Export failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'audit_log.csv';
    a.click();
    URL.revokeObjectURL(url);
  },

  async activateBlackout({ startDate, endDate, reason }) {
    return request('/admin/blackout', {
      method: 'POST',
      auth: true,
      body: { activate: true, start_date: startDate, end_date: endDate, reason: reason || '' },
    });
  },

  async deactivateBlackout() {
    return request('/admin/blackout', {
      method: 'POST',
      auth: true,
      body: { activate: false },
    });
  },

  async getParticipantActivity() {
    return request('/meta/participant/activity', { auth: true });
  },

  async getParticipantDocuments() {
    return request('/documents/participant', { auth: true });
  },

  async getAdminPlanActivity() {
    return request('/admin/activity', { auth: true });
  },

  async getAdminParticipants() {
    return request('/admin/participants', { auth: true });
  },

  async getAdminParticipantActivity(participantId) {
    return request(`/admin/participants/${participantId}/activity`, { auth: true });
  },

  async resetDemo() {
    return request('/admin/reset', { method: 'POST', auth: true });
  },

  async getParticipantInvestments() {
    return request('/meta/participant/investments', { auth: true });
  },

  async reallocateFunds({ scope, elections }) {
    return request('/transactions/reallocate', {
      method: 'POST',
      auth: true,
      body: { scope, elections },
    });
  },

  async getDeferralInfo() {
    return request('/meta/participant/deferral', { auth: true });
  },

  async changeDeferral({ newDeferralPct, deferralType, catchUp = false }) {
    return request('/transactions/change-deferral', {
      method: 'POST',
      auth: true,
      body: {
        new_deferral_pct: newDeferralPct,
        deferral_type:    deferralType,
        catch_up:         catchUp,
      },
    });
  },

  async updateAddress({ streetAddress, city, state, zipCode }) {
    return request('/transactions/address-update', {
      method: 'POST',
      auth: true,
      body: {
        street_address: streetAddress,
        city,
        state,
        zip_code: zipCode,
      },
    });
  },

  async getParticipantProfile() {
    return request('/meta/participant/profile', { auth: true });
  },

  async health() {
    return request('/health');
  },

  async chatFast(message, history = []) {
    const res = await fetch(`${BASE_URL}/chat/fast`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body: JSON.stringify({ message, history }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new ApiError(err?.detail || `Chat failed (${res.status})`, res.status);
    }
    return res.json();
  },

  async getPendingTransaction() {
    return request('/transactions/pending', { auth: true });
  },

  async confirmTransaction() {
    return request('/transactions/confirm', { method: 'POST', auth: true });
  },

  async cancelTransaction() {
    return request('/transactions/cancel', { method: 'POST', auth: true });
  },

  async disburseFunds({ routingNumber, accountNumber, accountType, entryId }) {
    const body = {
      routing_number: routingNumber,
      account_number: accountNumber,
      account_type: accountType,
    };
    if (entryId) body.entry_id = entryId;
    return request('/transactions/disburse', { method: 'POST', auth: true, body });
  },

  async getTransactionPending() {
    return request('/transactions/pending', { auth: true });
  },

  async uploadDocumentFast({ queueEntryId, actionType, expenseType, docType, file }) {
    const fd = new FormData();
    fd.append('queue_entry_id', String(queueEntryId));
    fd.append('action_type', actionType);
    fd.append('expense_type', expenseType || '');
    fd.append('doc_type', docType);
    fd.append('file', file);

    const response = await fetch(`${BASE_URL}/documents/upload-fast`, {
      method: 'POST',
      headers: { ...authHeader() },  // no Content-Type — browser sets multipart boundary
      body: fd,
    });

    let data = null;
    try { data = await response.json(); } catch { data = null; }
    if (!response.ok) {
      throw new ApiError(data?.detail || `Upload failed: ${response.status}`, response.status);
    }
    return data;
  },

  async downloadDocument(docId) {
    const response = await fetch(`${BASE_URL}/documents/${docId}/download`, {
      method: 'GET',
      headers: { ...authHeader() },
    });
    if (!response.ok) {
      throw new ApiError(`Download failed: ${response.status}`, response.status);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return blobUrl;
  },

  getPlanProfile: () => request('/admin/plan-profile', { auth: true }),

  getForm5500: () => request('/admin/form5500', { auth: true }),

  async downloadForm5500() {
    const session = getItem(STORAGE_KEYS.SESSION);
    const token = session?.sessionToken || '';
    const res = await fetch(`${BASE_URL}/admin/form5500/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Form 5500 export failed');
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const nameMatch = cd.match(/filename="([^"]+)"/);
    const filename = nameMatch ? nameMatch[1] : 'form5500.csv';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },

  applyPlanAmendment: (body) => request('/admin/plan/amendment', { method: 'PUT', auth: true, body }),
  getAmendmentLog: () => request('/admin/plan/amendment-log', { auth: true }),

  async downloadNotice(noticeType) {
    const session = getItem(STORAGE_KEYS.SESSION);
    const token = session?.sessionToken || '';
    const res = await fetch(`${BASE_URL}/admin/notices/${noticeType}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Notice generation failed');
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const nameMatch = cd.match(/filename="([^"]+)"/);
    const filename = nameMatch ? nameMatch[1] : `${noticeType}_notice.txt`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  },

  getNotifications: () => request('/notifications', { auth: true }),
  getUnreadCount: () => request('/notifications/unread-count', { auth: true }),
  markNotificationRead: (id) => request(`/notifications/${id}/read`, { method: 'POST', auth: true }),
  markAllNotificationsRead: () => request('/notifications/read-all', { method: 'POST', auth: true }),

  changePassword: (currentPassword, newPassword) =>
    request('/auth/change-password', {
      method: 'POST',
      auth: true,
      body: { current_password: currentPassword, new_password: newPassword },
    }),
};

export { ApiError };
